import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button, CircularProgress, Box, Typography, Paper } from "@material-ui/core";
import { useHistory, useLocation } from "react-router-dom";

export default function CheckoutSuccess() {
  const location = useLocation();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);

  const sessionId = new URLSearchParams(location.search).get("session_id");
  const API_BASE_URL = process.env.REACT_APP_BASE_URL || "";

  // This page is read-only. Only the signed Stripe webhook can finalize
  // onboarding and trigger the onboarding email.
  useEffect(() => {
    let mounted = true;

    async function loadSession() {
      if (!sessionId) {
        setError("No checkout session was found in the URL.");
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `${API_BASE_URL}stripe/session?sessionId=${encodeURIComponent(sessionId)}`
        );
        if (mounted) setSession(response.data);
      } catch (err) {
        if (!mounted) return;
        setError(
          err?.response?.data?.message ||
            "We could not retrieve your checkout information. Please contact support if your payment was completed."
        );
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadSession();
    return () => {
      mounted = false;
    };
  }, [API_BASE_URL, sessionId]);

  const handleGoToSignin = () => history.push("/signin");
  const paid =
    session?.paymentStatus === "paid" || session?.payment_status === "paid";

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh" p={2}>
        <Paper style={{ padding: 24, maxWidth: 720 }}>
          <Typography variant="h5">Checkout</Typography>
          <Typography color="error" style={{ marginTop: 12 }}>
            {error}
          </Typography>
          <Box mt={2}>
            <Button variant="contained" color="primary" onClick={() => history.push("/")}>
              Return home
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh" p={2}>
      <Paper style={{ padding: 28, maxWidth: 820 }}>
        <Typography variant="h4" gutterBottom>
          {paid ? "Payment successful" : "Payment confirmation pending"}
        </Typography>

        <Typography variant="body1" style={{ marginTop: 12 }}>
          {paid
            ? "Thank you. Stripe confirms your payment. The server will send your onboarding email after it verifies Stripe's webhook; delivery may take a few moments."
            : "Your checkout has returned, but payment has not yet been confirmed. The server sends the onboarding email only after Stripe confirms payment through its signed webhook. Please refresh this page shortly if the status has not updated."}
        </Typography>

        <Box mt={2}>
          <Typography variant="body2">
            <strong>Amount:</strong>{" "}
            {session?.amountTotal != null
              ? `${(session.amountTotal / 100).toFixed(2)} ${session.currency?.toUpperCase() || ""}`
              : "N/A"}
          </Typography>
        </Box>

        <Box mt={4} display="flex" gap={12} alignItems="center">
          <Button variant="contained" color="primary" onClick={handleGoToSignin}>
            Go to Sign in
          </Button>
          <Button
            variant="text"
            onClick={() => window.open("mailto:support@veiramal.com", "_self")}
          >
            Contact Support
          </Button>
        </Box>

        <Box mt={2}>
          <Typography variant="caption" color="textSecondary">
            For your security, this page cannot resend onboarding emails. Only the verified Stripe webhook can initiate onboarding after successful payment. If your email does not arrive, contact support.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
